import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(private http: HttpClient) {}

  private setHeaders(): HttpHeaders {
    const headersConfig = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    };

    return new HttpHeaders(headersConfig);
  }

  private formatErrors(error: any): Observable<never> {
    return throwError(() => new Error(JSON.stringify(error)));
  }

  private getBaseUrl(path: string, forceSecondary = false): string {
    // Se forçar, sempre usa a secundária
    // console.log('path', path, environment.apiProfissionaisUrl, environment.apiBolseirosUrl);

    if (forceSecondary) return environment.apiProfissionaisUrl;

    // Exemplo de lógica baseada no path
    const useSecondary = path.replace(/^\/+/, '').startsWith('profissionais');
    // console.log('useSecondary', useSecondary, 'path', path);
    return useSecondary
      ? environment.apiProfissionaisUrl
      : environment.apiBolseirosUrl;
  }

  private buildUrl(path: string, forceSecondary = false): string {
    const baseUrl = this.getBaseUrl(path, forceSecondary).replace(/\/+$/, '');
    const normalizedPath = path.replace(/^\/+/, '');
    return normalizedPath ? `${baseUrl}/${normalizedPath}` : baseUrl;
  }

  private buildConcursoUrl(path: string): string {
    const baseUrl = environment.apiConcursoUrl.replace(/\/+$/, '');
    const normalizedPath = path.replace(/^\/+/, '');
    return normalizedPath ? `${baseUrl}/${normalizedPath}` : baseUrl;
  }

  get<T>(
    path: string,
    httpParams: HttpParams = new HttpParams(),
    forceSecondary = false,
    responseType: 'json' | 'blob' = 'json',
  ): Observable<T> {
    return this.http
      .get<T>(this.buildUrl(path, forceSecondary), {
        headers: this.setHeaders(),
        params: httpParams,
        responseType: responseType as 'json', // necessário para tipagem correta
      })
      .pipe(catchError(this.formatErrors));
  }

  getConcurso<T>(path: string, httpParams: HttpParams = new HttpParams()): Observable<T> {
    return this.http
      .get<T>(this.buildConcursoUrl(path), {
        headers: this.setHeaders(),
        params: httpParams,
      })
      .pipe(catchError(this.formatErrors));
  }

  postConcurso<T>(path: string, body: Object = {}): Observable<T> {
    const isFormData = body instanceof FormData;
    return this.http
      .post<T>(this.buildConcursoUrl(path), body, {
        headers: isFormData ? undefined : this.setHeaders(),
      })
      .pipe(catchError(this.formatErrors));
  }

  put<T>(
    path: string,
    body: Object = {},
    params?: HttpParams,
    forceSecondary = false,
    customHeaders?: HttpHeaders,
  ): Observable<T> {
    //Detectar formData dentro da API
    const isFormData = body instanceof FormData;
    return this.http
      .put<T>(this.buildUrl(path, forceSecondary), body, {
        headers: isFormData ? undefined : customHeaders || this.setHeaders(),
        params,
      })
      .pipe(catchError(this.formatErrors));
  }

  putConcurso<T>(
    path: string,
    body: Object = {},
    params?: HttpParams,
    customHeaders?: HttpHeaders,
  ): Observable<T> {
    const isFormData = body instanceof FormData;
    return this.http
      .put<T>(this.buildConcursoUrl(path), body, {
        params,
        headers: isFormData ? undefined : customHeaders || this.setHeaders(),
      })
      .pipe(catchError(this.formatErrors));
  }

  post<T>(
    path: string,
    body: Object = {},
    options?: any,
    forceSecondary = false,
  ): Observable<any> {
    //Detectar formData dentro da API
    const isFormData = body instanceof FormData;

    return this.http
      .post<T>(this.buildUrl(path, forceSecondary), body, {
        ...options,
        // não definir headers se for FormData; respeita headers customizados quando fornecidos
        headers: isFormData
          ? undefined
          : options?.headers
            ? new HttpHeaders(options.headers)
            : this.setHeaders(),
      })
      .pipe(catchError(this.formatErrors));
  }

  postQueryParams<T>(
    path: string,
    body: Object = {},
    params?: HttpParams,
    forceSecondary = false,
    customHeaders?: HttpHeaders,
  ): Observable<T> {
    const isFormData = body instanceof FormData;

    return this.http
      .post<T>(this.buildUrl(path, forceSecondary), body, {
        params,
        headers: isFormData ? undefined : customHeaders || this.setHeaders(),
      })
      .pipe(catchError(this.formatErrors));
  }

  delete<T>(
    path: string,
    httpParams: HttpParams = new HttpParams(),
    forceSecondary = false,
  ): Observable<T> {
    return this.http
      .delete<T>(this.buildUrl(path, forceSecondary), {
        headers: this.setHeaders(),
        params: httpParams,
      })
      .pipe(catchError(this.formatErrors));
  }

  deleteConcurso<T>(
    path: string,
    httpParams: HttpParams = new HttpParams(),
  ): Observable<T> {
    return this.http
      .delete<T>(this.buildConcursoUrl(path), {
        headers: this.setHeaders(),
        params: httpParams,
      })
      .pipe(catchError(this.formatErrors));
  }

  patch<T>(
    path: string,
    body: Object = {},
    forceSecondary = false,
  ): Observable<T> {
    return this.http
      .patch<T>(this.buildUrl(path, forceSecondary), body, {
        headers: this.setHeaders(),
      })
      .pipe(catchError(this.formatErrors));
  }

  public download(
    path: string,
    httpParams: HttpParams = new HttpParams(),
    customHeaders = {},
    forceSecondary = false,
  ): Observable<Blob> {
    // const headers = new HttpHeaders({
    //   Authorization: token,
    //   ...customHeaders,
    // });

    // url = environment.app_url + url;

    return this.http.get<Blob>(this.buildUrl(path, forceSecondary), {
      headers: this.setHeaders(),
      params: httpParams,
      responseType: 'blob' as 'json',
    });
  }
}
